'use client';

import { useState, useCallback, useEffect } from 'react';

/**
 * Hook personalizado para manejar la lógica de navegación del Sidenav
 * Gestiona estados activos, elementos expandidos y navegación jerárquica
 */
export const useNavigation = (navigationItems = []) => {
  // Estado para el elemento activo actual
  const [activeItem, setActiveItem] = useState(null);
  
  // Estado para elementos expandidos (para navegación jerárquica)
  const [expandedItems, setExpandedItems] = useState(new Set());

  // Inicializar elemento activo basado en navigationItems
  useEffect(() => {
    if (navigationItems.length > 0 && !activeItem) {
      // Buscar el primer elemento marcado como activo
      const findActiveItem = (items) => {
        for (const item of items) {
          if (item.isActive) {
            return item.id;
          }
          if (item.children) {
            const childActive = findActiveItem(item.children);
            if (childActive) {
              // Si un hijo está activo, expandir el padre
              setExpandedItems(prev => new Set([...prev, item.id]));
              return childActive;
            }
          }
        }
        return null;
      };

      const active = findActiveItem(navigationItems) || navigationItems[0]?.id;
      if (active) {
        setActiveItem(active);
      }
    }
  }, [navigationItems, activeItem]);

  // Función para alternar expansión de elementos con hijos
  const toggleExpanded = useCallback((itemId) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  // Función para establecer elemento activo
  const setActiveItemHandler = useCallback((itemId) => {
    setActiveItem(itemId);
  }, []);

  // Función para manejar navegación (click en elemento)
  const handleNavigation = useCallback((item, onItemClick) => {
    // Establecer como activo
    setActiveItem(item.id);

    // Si tiene hijos, alternar expansión
    if (item.children && item.children.length > 0) {
      toggleExpanded(item.id);
    }

    // Ejecutar callback personalizado si existe
    if (item.onClick) {
      item.onClick(item);
    }

    // Ejecutar callback del componente padre si existe
    if (onItemClick) {
      onItemClick(item);
    }

    // Navegación por href si existe
    if (item.href && !item.children) {
      // En un entorno real, aquí se usaría el router de la aplicación
      // Por ejemplo: navigate(item.href) para React Router
      console.log(`Navigating to: ${item.href}`);
    }
  }, [toggleExpanded]);

  // Función para verificar si un elemento está expandido
  const isExpanded = useCallback((itemId) => {
    return expandedItems.has(itemId);
  }, [expandedItems]);

  // Función para verificar si un elemento está activo
  const isActive = useCallback((itemId) => {
    return activeItem === itemId;
  }, [activeItem]);

  // Función para encontrar un elemento por ID (útil para navegación programática)
  const findItemById = useCallback((itemId, items = navigationItems) => {
    for (const item of items) {
      if (item.id === itemId) {
        return item;
      }
      if (item.children) {
        const found = findItemById(itemId, item.children);
        if (found) return found;
      }
    }
    return null;
  }, [navigationItems]);

  // Función para obtener la ruta completa de un elemento (breadcrumb)
  const getItemPath = useCallback((itemId, items = navigationItems, path = []) => {
    for (const item of items) {
      const currentPath = [...path, item];
      
      if (item.id === itemId) {
        return currentPath;
      }
      
      if (item.children) {
        const found = getItemPath(itemId, item.children, currentPath);
        if (found) return found;
      }
    }
    return null;
  }, [navigationItems]);

  // Función para expandir todos los padres de un elemento específico
  const expandToItem = useCallback((itemId) => {
    const path = getItemPath(itemId);
    if (path) {
      const newExpanded = new Set(expandedItems);
      // Expandir todos los elementos padre (excepto el último que es el elemento objetivo)
      path.slice(0, -1).forEach(item => {
        if (item.children && item.children.length > 0) {
          newExpanded.add(item.id);
        }
      });
      setExpandedItems(newExpanded);
    }
  }, [expandedItems, getItemPath]);

  // Función para colapsar todos los elementos
  const collapseAll = useCallback(() => {
    setExpandedItems(new Set());
  }, []);

  // Función para expandir todos los elementos que tienen hijos
  const expandAll = useCallback(() => {
    const getAllParentIds = (items) => {
      const parentIds = [];
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          parentIds.push(item.id);
          parentIds.push(...getAllParentIds(item.children));
        }
      });
      return parentIds;
    };

    const allParentIds = getAllParentIds(navigationItems);
    setExpandedItems(new Set(allParentIds));
  }, [navigationItems]);

  return {
    // Estados
    activeItem,
    expandedItems: Array.from(expandedItems),
    
    // Funciones de verificación
    isExpanded,
    isActive,
    
    // Funciones de manipulación
    toggleExpanded,
    setActiveItem: setActiveItemHandler,
    handleNavigation,
    
    // Funciones de utilidad
    findItemById,
    getItemPath,
    expandToItem,
    collapseAll,
    expandAll
  };
};

export default useNavigation;